/**
 * QuoteInternalSummary — ops column (mock: Internal + Financial Snapshot).
 */

import React, { useMemo } from "react";
import { Box, Card, CardContent, Divider, Grid, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  formatQuoteMoney,
  getQuoteInternalSnapshot,
} from "../services/quoteOperationalStatus.js";
import QuotePricingExplanationTree from "./QuotePricingExplanationTree.jsx";
import VolumeMetricHelpBubble from "./VolumeMetricHelpBubble.jsx";

const Panel = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  height: "100%",
}));

const Field = ({ label, value, labelHelpMetric }) => {
  const display =
    value == null || value === ""
      ? "—"
      : typeof value === "object"
        ? "—"
        : String(value);

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {labelHelpMetric ? <VolumeMetricHelpBubble metric={labelHelpMetric} /> : null}
      </Stack>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {display}
      </Typography>
    </Box>
  );
};

const FinanceStat = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: 10,
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.divider}`,
}));

const QuoteInternalSummary = ({
  quote,
  showExplanationTree = false,
  hideFinance = false,
}) => {
  const snap = useMemo(() => getQuoteInternalSnapshot(quote), [quote]);
  const explanation = quote?.automatedPreparation?.pricingExplanation || null;

  return (
    <Box data-testid="quote-internal-summary">
      <Panel elevation={0}>
        <CardContent sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Fulfilment snapshot
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={2}>
            Not visible to the customer
          </Typography>

          {showExplanationTree && explanation ? (
            <QuotePricingExplanationTree explanation={explanation} />
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Field label="Pricing engine" value={snap.engineLabel} />
            </Grid>
            <Grid item xs={6}>
              <Field label="Corridor" value={snap.corridor || "—"} />
            </Grid>
            <Grid item xs={6}>
              <Field
                label="Billable volume"
                labelHelpMetric="billable"
                value={
                  snap.billableVolumeM3 != null
                    ? `${Number(snap.billableVolumeM3).toFixed(2)} m³`
                    : "—"
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Field
                label="Rate per m³"
                value={
                  snap.ratePerM3 != null
                    ? formatQuoteMoney(snap.ratePerM3)
                    : "—"
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Field
                label="Corridor minimum"
                value={
                  snap.minimumAmount != null
                    ? formatQuoteMoney(snap.minimumAmount)
                    : "—"
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Field
                label="Transport amount"
                value={formatQuoteMoney(snap.transportAmount)}
              />
            </Grid>
            <Grid item xs={6}>
              <Field
                label="Rounding"
                value={
                  snap.roundingAdjustment != null
                    ? formatQuoteMoney(snap.roundingAdjustment)
                    : "—"
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Field label="Truck" value={snap.truckSize || "—"} />
            </Grid>
            <Grid item xs={6}>
              <Field label="Move type" value={snap.moveTypeLabel || "—"} />
            </Grid>
          </Grid>
        </CardContent>
      </Panel>

      {!hideFinance && (
      <Panel elevation={0}>
        <CardContent sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }} mb={1.5}>
            Financial snapshot
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <FinanceStat>
                <Typography variant="caption" color="text.secondary">
                  Est. provider cost
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {snap.expectedProviderCost != null
                    ? formatQuoteMoney(snap.expectedProviderCost)
                    : "—"}
                </Typography>
              </FinanceStat>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FinanceStat>
                <Typography variant="caption" color="text.secondary">
                  Est. Bubakii revenue
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "success.dark" }}
                >
                  {snap.revenue != null ? formatQuoteMoney(snap.revenue) : "—"}
                </Typography>
              </FinanceStat>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FinanceStat>
                <Typography variant="caption" color="text.secondary">
                  Gross margin
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: "primary.main" }}
                >
                  {snap.marginPercent != null ? `${snap.marginPercent}%` : "—"}
                </Typography>
              </FinanceStat>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Provider bidding and fulfilment planning live on the Lead — not on
            this customer quote.
          </Typography>
        </CardContent>
      </Panel>
      )}
    </Box>
  );
};

export default QuoteInternalSummary;
